import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import React, { useEffect, useId, useState } from 'react';
import { Stack } from '../Layout';
import { theme } from '../theme';
import { renderWithTooltip } from '../Tooltip';
import { LabelWithHint } from '../Typography/LabelWithHint';
import { Text } from '../Typography/Text';
import { StyledSwitchContainer, StyledSwitchRoot, StyledSwitchThumb } from './styled';
import { tokens } from './tokens';
import { SwitchSize, SwitchVariants } from './types';
export function Switch({ checked, defaultChecked, size = SwitchSize.sm, disabled, onChange, children, hint, tooltip, labelPosition = 'right', helpText, allowIndeterminateByClick, canChange, variant = SwitchVariants.primary, 'data-test-id': dataTestId, id: propId, 'aria-label': ariaLabel, thumbIcon, ...restProps }) {
    const generatedId = useId();
    const switchId = propId ?? generatedId;
    const [toggle, setToggle] = useState(checked);
    useEffect(()=>{
        setToggle(checked);
    }, [
        checked
    ]);
    const onSwitchChange = usePersistentCallback(async ()=>{
        if (disabled) return;
        if (canChange) {
            const result = await canChange();
            if (!result) {
                return;
            }
        }
        const prevValue = toggle;
        let nextValue;
        if (prevValue === false) {
            nextValue = allowIndeterminateByClick ? undefined : true;
        } else if (prevValue === undefined) {
            nextValue = true;
        } else if (prevValue === true) {
            nextValue = false;
        }
        const allowChange = onChange?.(nextValue);
        if (allowChange) {
            setToggle(nextValue);
        }
    });
    const content = tooltip ? renderWithTooltip(/*#__PURE__*/ _jsx("div", {
        children: children
    }), tooltip) : children;
    let childrenUI = content ? /*#__PURE__*/ _jsx(Text, {
        elementType: "label",
        ...{
            htmlFor: switchId
        },
        level: "sm",
        weight: tokens.Label.weight,
        tone: disabled ? tokens.Label.disabled.color : tokens.Label.color,
        onClick: onSwitchChange,
        children: content
    }) : null;
    if (hint) {
        childrenUI = /*#__PURE__*/ _jsx(LabelWithHint, {
            hint: hint,
            children: childrenUI
        });
    }
    let switchRootUI = /*#__PURE__*/ _jsx(StyledSwitchRoot, {
        id: switchId,
        size: size,
        checked: Boolean(toggle),
        defaultChecked: defaultChecked,
        onClick: onSwitchChange,
        disabled: Boolean(disabled),
        hasHelpText: Boolean(helpText),
        "aria-label": ariaLabel,
        "data-test-id": dataTestId,
        ...restProps,
        children: /*#__PURE__*/ _jsx(StyledSwitchThumb, {
            size: size,
            disabled: disabled,
            children: thumbIcon
        })
    });
    const finalContent = helpText ? /*#__PURE__*/ _jsxs(Stack, {
        children: [
            childrenUI,
            /*#__PURE__*/ _jsx(Text, {
                level: "xs",
                tone: tokens.Label[variant].helpTextColor,
                children: helpText
            })
        ]
    }) : childrenUI;
    let switchUI = /*#__PURE__*/ _jsxs(StyledSwitchContainer, {
        direction: "horizontal",
        gap: theme.spaces.x10,
        alignY: helpText ? 'start' : 'center',
        disabled: Boolean(disabled),
        "data-test-id": `${dataTestId}-container`,
        children: [
            labelPosition === 'left' ? finalContent : null,
            switchRootUI,
            labelPosition === 'right' ? finalContent : null
        ]
    });
    return switchUI;
}
export * from './types';
